import { type ReactNode, useEffect, useRef } from 'react';
import { useBackLayer } from '../hooks/useBackLayer';

interface Props {
  children: ReactNode;
  /** Close on backdrop tap, Escape and the browser back button. Default true. */
  dismissible?: boolean;
  onClose?: () => void;
  tall?: boolean;
  label: string;
}

/** Modal bottom sheet. Non-dismissible sheets (the find sheet) only close through their own actions. */
export function Sheet({ children, dismissible = true, onClose, tall = false, label }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
    if (!dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismissible, onClose]);

  return (
    <div className="backdrop" onClick={dismissible ? onClose : undefined} data-testid="sheet-backdrop">
      {dismissible && <BackLayer onBack={() => onClose?.()} />}
      <div ref={ref} className={`sheet${tall ? ' tall' : ''}`} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <div className="handle" />
        {children}
      </div>
    </div>
  );
}

function BackLayer({ onBack }: { onBack: () => void }) {
  useBackLayer(onBack);
  return null;
}
